import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CheckCircleFilled, DeleteOutlined, ExclamationCircleOutlined, MinusCircleFilled, TranslationOutlined } from "@ant-design/icons";
import { Button, Col, Collapse, Form, Popconfirm, Popover, Row, Select, Space, Table, Tag, Typography } from "antd";
import { useMemo } from "react";
import { ETranslationFlag, TRANSLATIONS_PROPERTY_NAME } from "@devowl-wp/api";
import { DEFAULT_LANGUAGE, EUserRole, TemplateMetaDataField, isLanguageAllowed } from "@devowl-wp/api-real-cookie-banner";
import { fetchTranslations } from "../../../../../../../hooks/useTranslation.js";
import { useTranslation } from "../../../../../../../others/i18n.js";
import { useStores } from "../../../../../../../store/root.js";
import { colors } from "../../../../../../../styles/colors.js";
import { spacing } from "../../../../../../../styles/spacing.js";
import { DEFAULT_PAGINATION_META_DATA } from "../../../../../../../types/template/meta-data.js";
import { EFormEvent, FormEventBus } from "../../../../../../../util/eventBus.js";
import { getPathInfo, resolveFlexNamePath } from "../../../../../../../util/template/form.js";
import { getSupportedLanguageOptions } from "../../../../../../../util/template/translation.js";
import { getTranslationFlagNamePath, onTranslationValueChange } from "../../../../../../../util/translation.js";
import { FormItemHiddenSerialized } from "../../../../../../custom/form-hidden-serialized.js";
import { FORM_FIELD_TEMPLATE_TYPE_FIELD_NAME } from "../../template-type.js";
import { useLanguageOptions } from "../language-select.js";
import { Legend } from "../translation-legend.js";
import { UntranslatableHeader } from "../untranslatable-header.js";
import { UntranslatableManager } from "../untranslatable-manager.js";
/**
 * Renders a form item that allows users to select translation flags from a dropdown. The component is conditionally visible
 * and uses a serialized hidden input to manage the state of the selected flags.
 *
 * @param props - The properties for configuring the translation flags selector.
 * @param props.flexPathConfig - Configuration
 * @param props.visible - Controls the visibility of the form item.
 * @param props.lang - Target language for which it's displayed.
 */ function TranslationFlagsSelector({ flexPathConfig, visible, lang, hiddenFlags }) {
    const { t } = useTranslation();
    const form = Form.useFormInstance();
    const { rcbAuthenticationStore, rcbUserSettingsStore: { settings: { translationSettings: { isTranslationMode } } }, notificationStore } = useStores();
    const { session } = rcbAuthenticationStore;
    const languages = useMemo(()=>useLanguageOptions(t), [
        t
    ]);
    const inputInfo = getPathInfo(flexPathConfig, lang);
    const resetTranslation = (record)=>{
        form.setFieldValue(record.namePath, null);
        onTranslationValueChange(form, record.namePath, record.language);
        record.hasTranslation = false;
    };
    const triggerFormChange = ()=>{
        FormEventBus.publish(EFormEvent.TRANSLATION_FLAGS_CHANGED);
    };
    const isTranslatorAllowed = rcbAuthenticationStore.hasRole(EUserRole.Translator) && lang !== DEFAULT_LANGUAGE && isLanguageAllowed(session.user, lang);
    const isCreator = rcbAuthenticationStore.hasRole(EUserRole.Creator);
    const requestTranslation = async (record, checkTargetLanguageTranslationHasStarted = false)=>{
        const targetLanguage = form.getFieldValue([
            TRANSLATIONS_PROPERTY_NAME,
            record.language
        ]);
        const translationHasStarted = targetLanguage && Object.keys(targetLanguage).length > 0;
        if (checkTargetLanguageTranslationHasStarted === false || translationHasStarted) {
            const namePath = resolveFlexNamePath(inputInfo.translationNamePath, record.language);
            const flagNamePath = getTranslationFlagNamePath(namePath);
            const sourceNamePath = inputInfo.translationNamePathDefaultLang;
            const sourceValue = form.getFieldValue(sourceNamePath);
            console.log("sourceValue", {
                sourceValue
            });
            let resultValue = structuredClone(sourceValue);
            const templateType = form.getFieldValue(FORM_FIELD_TEMPLATE_TYPE_FIELD_NAME);
            const templateIdentifier = form.getFieldValue(TemplateMetaDataField.Identifier);
            const response = await fetchTranslations(record.language, [
                sourceValue
            ], rcbAuthenticationStore, {
                templateType,
                templateIdentifier
            });
            if (!response.result.isSupportedTargetLanguage) {
                notificationStore.add({
                    type: "info",
                    title: t("Machine translation skipped"),
                    message: t(`Translations for ${languages.find((language)=>language.value === record.language)?.label} is not supported, yet.`),
                    closable: true
                });
                return;
            }
            resultValue = response.result?.translations?.[0];
            form.setFieldValue(namePath, resultValue);
            form.setFieldValue(flagNamePath, [
                ETranslationFlag.MachineTranslated
            ]);
        }
    };
    // remove default lang and filter allowed languages
    const langs = useMemo(()=>getSupportedLanguageOptions(t).filter((option)=>option.language !== DEFAULT_LANGUAGE && isLanguageAllowed(session.user, option.language)), [
        t,
        session.user
    ]);
    const showTranslationTable = lang === DEFAULT_LANGUAGE && isTranslationMode;
    const dataSource = showTranslationTable ? langs.map((option)=>{
        const targetNamePath = inputInfo.translationNamePath || inputInfo.namePath;
        const langNamePath = resolveFlexNamePath(targetNamePath, option.language);
        const langFlagNamePath = getTranslationFlagNamePath(langNamePath);
        const fieldValue = form.getFieldValue(langNamePath);
        const fieldFlagValue = form.getFieldValue(langFlagNamePath);
        return {
            lang: option.icon,
            hasTranslation: !!fieldValue,
            isFlagged: Array.isArray(fieldFlagValue) && fieldFlagValue.length > 0,
            namePath: langNamePath,
            language: option.language
        };
    }) : [];
    const columns = [
        {
            dataIndex: "lang",
            title: t("Language"),
            align: "center"
        },
        {
            dataIndex: "hasTranslation",
            title: t("Translated"),
            sorter: (a, b)=>{
                const aNum = a.hasTranslation ? 1 : 0;
                const bNum = b.hasTranslation ? 1 : 0;
                return aNum - bNum;
            },
            defaultSortOrder: "descend",
            render: (value)=>{
                if (value) {
                    return /*#__PURE__*/ _jsx(CheckCircleFilled, {
                        style: {
                            color: "lightgreen"
                        },
                        title: t("Has translation")
                    });
                } else {
                    return /*#__PURE__*/ _jsx(ExclamationCircleOutlined, {
                        style: {
                            color: "red"
                        },
                        title: t("Missing translation")
                    });
                }
            },
            align: "center"
        },
        {
            dataIndex: "isFlagged",
            title: t("Flagged"),
            sorter: (a, b)=>{
                const aNum = a.isFlagged ? 1 : 0;
                const bNum = b.isFlagged ? 1 : 0;
                return aNum - bNum;
            },
            defaultSortOrder: "descend",
            render: (value)=>{
                if (value) {
                    return /*#__PURE__*/ _jsx(CheckCircleFilled, {
                        style: {
                            color: "lightgreen"
                        },
                        title: t("Has flags")
                    });
                } else {
                    return /*#__PURE__*/ _jsx(MinusCircleFilled, {
                        style: {
                            color: "lightgray"
                        },
                        title: t("No flags")
                    });
                }
            },
            align: "center"
        }
    ];
    if (!flexPathConfig.isDistinctFlagPath) {
        columns.push({
            dataIndex: "action",
            title: t("Action"),
            render: (_, record)=>{
                const disableTranslate = Object.keys(form.getFieldValue([
                    TRANSLATIONS_PROPERTY_NAME,
                    record.language
                ]) || {}).length === 0;
                const disableDeletion = !record.hasTranslation && !record.isFlagged;
                return /*#__PURE__*/ _jsxs(Space, {
                    size: "small",
                    children: [
                        /*#__PURE__*/ _jsx(Popconfirm, {
                            title: t("Translation"),
                            description: t("Are you sure to overwrite current translation?"),
                            okText: t("Translate"),
                            onConfirm: ()=>requestTranslation(record),
                            children: /*#__PURE__*/ _jsx(Popover, {
                                placement: "left",
                                title: disableTranslate ? t("Translations in this language have not yet begun") : undefined,
                                content: disableTranslate ? t("Please either let all fields be translated by machine or translate them manually before machine translating single fields!") : t("Translate"),
                                children: /*#__PURE__*/ _jsx(Button, {
                                    icon: /*#__PURE__*/ _jsx(TranslationOutlined, {}),
                                    size: "small",
                                    title: t("Translate"),
                                    type: "primary",
                                    onDoubleClick: ()=>requestTranslation(record),
                                    disabled: disableTranslate
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx(Popconfirm, {
                            title: t("Translation and flags"),
                            description: t("Delete translation and flags?"),
                            okText: t("Delete"),
                            onConfirm: ()=>{
                                resetTranslation(record);
                                triggerFormChange();
                            },
                            children: /*#__PURE__*/ _jsx(Popover, {
                                placement: "left",
                                content: t("Delete"),
                                children: /*#__PURE__*/ _jsx(Button, {
                                    icon: /*#__PURE__*/ _jsx(DeleteOutlined, {}),
                                    size: "small",
                                    type: "primary",
                                    onDoubleClick: ()=>{
                                        resetTranslation(record);
                                        triggerFormChange();
                                    },
                                    disabled: disableDeletion
                                })
                            })
                        })
                    ]
                });
            },
            align: "center",
            width: "15em"
        });
    }
    const flgInputPath = flexPathConfig.isDistinctFlagPath ? inputInfo.flagNamePath : inputInfo.namePath;
    const translationFlagSelectNamePath = getTranslationFlagNamePath(flgInputPath);
    const flagTagRender = (props)=>{
        const { label, value, closable, onClose } = props;
        const onPreventMouseDown = (event)=>{
            event.preventDefault();
            event.stopPropagation();
        };
        let color;
        switch(value){
            case ETranslationFlag.Fuzzy:
                color = colors.translation.fuzzy;
                break;
            case ETranslationFlag.MachineTranslated:
                color = colors.translation.machineTranslated;
                break;
            default:
                color = colors.translation.hasFlags;
                break;
        }
        return /*#__PURE__*/ _jsx(Tag, {
            color: color,
            onMouseDown: onPreventMouseDown,
            closable: closable,
            onClose: onClose,
            style: {
                marginInlineEnd: 4
            },
            children: label
        });
    };
    let flagSelectOptions = [
        {
            label: t("Fuzzy"),
            value: ETranslationFlag.Fuzzy
        },
        {
            label: t("Machine-Translated"),
            value: ETranslationFlag.MachineTranslated
        }
    ];
    flagSelectOptions = flagSelectOptions.filter((option)=>!hiddenFlags?.includes(option.value));
    const itemValue = form.getFieldValue(inputInfo.flagNamePath || inputInfo.namePath);
    if (!itemValue) {
        flagSelectOptions = flagSelectOptions.filter((option)=>option.value !== ETranslationFlag.MachineTranslated);
    }
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(FormItemHiddenSerialized, {
                name: translationFlagSelectNamePath,
                visible: visible,
                visibleFormProps: {
                    label: t("Translation flags")
                },
                children: /*#__PURE__*/ _jsx(Select, {
                    disabled: !isCreator && !isTranslatorAllowed,
                    options: flagSelectOptions,
                    allowClear: true,
                    mode: "multiple",
                    style: {
                        width: 300
                    },
                    tagRender: flagTagRender
                })
            }),
            showTranslationTable && visible && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(Row, {
                        children: [
                            /*#__PURE__*/ _jsx(Col, {
                                flex: "1",
                                children: /*#__PURE__*/ _jsx(Typography.Title, {
                                    level: 5,
                                    style: {
                                        margin: 0
                                    },
                                    children: t("Translations / Flagging")
                                })
                            }),
                            /*#__PURE__*/ _jsxs(Col, {
                                style: {
                                    textAlign: "right"
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(Popconfirm, {
                                        title: t("Translate all"),
                                        description: t("Are you sure to overwrite all translations?"),
                                        okText: t("Translate"),
                                        onConfirm: ()=>dataSource.forEach((entry)=>requestTranslation(entry, true)),
                                        children: /*#__PURE__*/ _jsx(Button, {
                                            hidden: !!flexPathConfig.isDistinctFlagPath,
                                            icon: /*#__PURE__*/ _jsx(TranslationOutlined, {}),
                                            disabled: !dataSource.map((e)=>e.hasTranslation).reduce((prev, next)=>prev || next),
                                            type: "link",
                                            children: t("Translate all")
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Popconfirm, {
                                        title: t("Delete all"),
                                        description: t("Delete all other translations and flags?"),
                                        okText: t("Delete"),
                                        onConfirm: ()=>{
                                            dataSource.forEach((entry)=>resetTranslation(entry));
                                            triggerFormChange();
                                        },
                                        children: /*#__PURE__*/ _jsx(Button, {
                                            hidden: !!flexPathConfig.isDistinctFlagPath,
                                            icon: /*#__PURE__*/ _jsx(DeleteOutlined, {}),
                                            disabled: !dataSource.map((e)=>e.hasTranslation).reduce((prev, next)=>prev || next),
                                            type: "link",
                                            children: t("Delete all")
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Row, {
                        children: /*#__PURE__*/ _jsx(Col, {
                            children: /*#__PURE__*/ _jsx(Table, {
                                style: {
                                    width: "600px",
                                    textAlign: "center"
                                },
                                scroll: {
                                    y: "350px"
                                },
                                size: "small",
                                columns: columns,
                                dataSource: dataSource,
                                pagination: DEFAULT_PAGINATION_META_DATA
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(Legend, {
                        style: {
                            margin: spacing.layout.content.marginMedium,
                            justifyContent: "flex-end"
                        },
                        hidden: false,
                        items: [
                            {
                                icon: /*#__PURE__*/ _jsx(ExclamationCircleOutlined, {
                                    style: {
                                        color: "red"
                                    }
                                }),
                                text: t("Missing")
                            },
                            {
                                icon: /*#__PURE__*/ _jsx(CheckCircleFilled, {
                                    style: {
                                        color: "lightgreen"
                                    }
                                }),
                                text: t("Valid")
                            },
                            {
                                icon: /*#__PURE__*/ _jsx(MinusCircleFilled, {
                                    style: {
                                        color: "lightgray"
                                    }
                                }),
                                text: t("No flags")
                            }
                        ]
                    }),
                    (()=>{
                        const templateType = form.getFieldValue(FORM_FIELD_TEMPLATE_TYPE_FIELD_NAME);
                        const templateIdentifier = form.getFieldValue(TemplateMetaDataField.Identifier);
                        if (!templateType || !templateIdentifier) return null;
                        return /*#__PURE__*/ _jsx(Collapse, {
                            size: "small",
                            style: {
                                marginTop: 16
                            },
                            children: /*#__PURE__*/ _jsx(Collapse.Panel, {
                                header: /*#__PURE__*/ _jsx(UntranslatableHeader, {
                                    level: 5,
                                    style: {
                                        marginBottom: 0
                                    }
                                }),
                                children: /*#__PURE__*/ _jsx(UntranslatableManager, {
                                    templateType: templateType,
                                    templateIdentifier: templateIdentifier
                                })
                            }, "untranslatables")
                        });
                    })()
                ]
            })
        ]
    });
}
export { TranslationFlagsSelector };
